<template lang="pug">
yz-filter-menu(:label="$t('group')" :is-active="selectedItems.length > 0" :display-value="displayValue" :loading="isLoadingInit" :max-height="500" :show-header="false" @update:menu="onMenuOpen" @clear="onClear" @apply="onApply")
  .menu-header.sticky.top-0.z-1
    .font-semibold.text-sm.pa-4 {{ $t('filter_by') }} {{ $t('group').toLowerCase() }}
    yz-text-field.text-sm.mx-4.mb-1(v-model="searchText" :placeholder="$t('search')" size="lg" @input="onQueryChanged")
    .d-flex.align-center.justify-end.px-4.py-2(v-if="count > pageSize")
      yz-pagination(v-model="page" :page-size="pageSize" :count="count" :loading="isLoading" arrow type="item")

    yz-divider

  .scrollable-content
    yz-skeleton-loader.pa-4(v-if="isLoading || isLoadingInit" type="text@3")

    div(v-else-if="error")
      .text-center.py-8
        .text-xs.neutral500--text.mb-3 {{ $t('error_message.error_occured') }}
        yz-btn(size="sm" variant="outlined" color="primary" @click="fetchGroups") {{ $t('retry') }}

    div(v-else)
      template(v-if="groups.length > 0")
        div(v-for="group in groups" :key="group.value")
          .d-flex.text-sm.px-4.py-3.cursor-pointer.hover-bg(@click="onClickItem(group)")
            yz-checkbox(:input-value="isChecked(group.value)" @change="onClickItem(group)")
            .ml-4 {{ group.label }}
          yz-divider.my-0
      template(v-else)
        .text-center.py-8.text-xs.neutral500--text No groups found
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    filter: { type: Object, default: () => ({}) },
    checkInitialValue: { type: Boolean, default: false }
  },
  data: () => ({
    isLoading: false,
    isLoadingInit: false,
    searchText: '',
    groups: [],
    checkedItems: [],
    selectedItems: [],
    groupsLoaded: false,
    error: false,
    page: 1,
    pageSize: 10,
    count: 0
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    displayValue () {
      const count = this.selectedItems.length
      if (count <= 2) return this.selectedItems.map(x => x.label).join(', ')
      return `${count} selected`
    }
  },
  watch: {
    filter: {
      handler () {
        if (!this.filter.key) this.clearData()
      }
    },
    page () {
      this.fetchGroups()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    onMenuOpen (isOpen) {
      if (isOpen && !this.groupsLoaded) {
        this.fetchGroups()
        this.setData()
      }
    },
    async setData () {
      if (this.checkInitialValue && this.filter.key) {
        this.isLoadingInit = true
        const ids = this.filter.value.split(',')
        try {
          const items = []
          for (let i = 0; i < ids.length; i++) {
            const resp = await this.$axios.get(`/options/${ids[i]}`)
            items.push({ label: resp.data.value, value: `${resp.data.id}` })
          }
          this.checkedItems = JSON.parse(JSON.stringify(items))
          this.selectedItems = JSON.parse(JSON.stringify(items))
        } catch {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        } finally {
          this.isLoadingInit = false
        }
      }
    },
    async fetchGroups () {
      this.isLoading = true
      this.error = false
      try {
        const params = {
          field: 'medication.group',
          page: this.page,
          page_size: this.pageSize,
          search: this.searchText || ''
        }
        const resp = await this.$axios.get(`/options?company=${this.company.id}`, { params })
        this.groups = resp.data?.results
          ? resp.data.results.map(item => ({ label: item.value, value: `${item.id}` }))
          : []
        this.count = resp.data?.count || 0
        this.groupsLoaded = true
      } catch {
        this.groups = []
        this.error = true
      } finally {
        this.isLoading = false
      }
    },
    onClear () {
      this.clearData()
      this.$emit('update:filter', {})
    },
    clearData () {
      this.checkedItems = []
      this.selectedItems = []
    },
    onApply () {
      this.selectedItems = [...this.checkedItems]
      this.$emit('update:filter', { key: 'groups', value: this.checkedItems.map(i => i.value).join(',') })
    },
    onClickItem (item) {
      if (this.checkedItems.map(i => i.value).includes(item.value)) {
        this.checkedItems = this.checkedItems.filter(x => x.value !== item.value)
      } else {
        this.checkedItems.push(item)
      }
    },
    onQueryChanged: debounce(function () {
      this.page = 1
      this.fetchGroups()
    }, 750),
    isChecked (value) {
      return this.checkedItems.some(item => item.value === value)
    }
  }
}
</script>

<style scoped>
.hover-bg:hover {
  background-color: #f5f5f5;
}

.menu-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  overflow: hidden;
}

.scrollable-content {
  max-height: 300px;
  overflow-y: auto;
}

.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #b0b0b0;
}
</style>
